import { ThunkResult } from "common/reducers";
import { RootDispatchType, RootGetState } from "common/store";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { batchCheck, getAccess, list, updateAccess } from "common/api/access.service";
import { List } from "immutable";
import { AccessActions } from "common/reducers/access.actions";
import { UserActions } from "common/reducers/user.actions";
import { handleErrors } from "common/reducers/Utils";
import { getRecordsByModel } from "common/selectors/idsManagement.selectors";
import { AccessRecord } from "common/records/Access";
import { UserAsyncActions } from "common/reducers/user.async.actions";
import { accessById } from "common/selectors/access.selectors";
import { addFlashAlert } from "common/components/FlashAlert";
import { AccessParamsRecord } from "common/records/index/AccessParams";
import { watchRedux } from "src/router/loaders/helper";
import { userLoaded } from "common/selectors/user.selectors";
import { IGlobalRequestOptions } from "common/api/Fetcher";

export const AccessAsyncActions = {
  listAccesses: (
    queryParams: AccessParamsRecord,
    waitForUser = false,
    loadAcl = false,
    options?: IGlobalRequestOptions,
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      try {
        const userId = queryParams.get("userId");
        if (waitForUser) await watchRedux((state) => userLoaded(state, { userId }));

        dispatch(IdsManagementActions.fetchModel("accesses", queryParams));

        const { accesses, meta } = await (options ? list(queryParams, options) : list(queryParams));

        dispatch(AccessActions.receiveAccesses(accesses, false));
        dispatch(
          UserActions.addAccesses(
            userId,
            accesses.map((a) => a.id),
          ),
        );
        dispatch(IdsManagementActions.receiveModel("accesses", List(accesses.map((a) => a.id)), meta.pagination));

        const accessesList: List<AccessRecord> = getRecordsByModel(getState(), {
          modelName: "accesses",
          path: ["accesses", "byId"],
        });

        if (loadAcl) {
          accessesList.forEach((access) => {
            dispatch(UserAsyncActions.loadAllowedAcl(userId, "", ""));
            dispatch(UserAsyncActions.loadAllowedAcl(userId, access.uid, "editable"));
            dispatch(UserAsyncActions.loadAllowedAcl(userId, access.uid, ""));
          });
        }
      } catch (errors) {
        handleErrors(errors);
        dispatch(IdsManagementActions.receiveModel("accesses", List()));
      }
    };
  },
  getAccess: (userId: number, orgId: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      try {
        dispatch(IdsManagementActions.fetchModel("accesses"));
        const { access } = await getAccess(userId, orgId);
        dispatch(AccessActions.setAccess(access.id, access));
      } catch (errors) {
        handleErrors(errors);
      } finally {
        dispatch(IdsManagementActions.setModelLoaded("accesses"));
      }
    };
  },
  updateAccess: (accessId: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      try {
        dispatch(IdsManagementActions.fetchModel("accesses"));
        const config = accessById(getState(), { accessId })?.get("config");
        const { access } = await updateAccess(accessId, config);

        dispatch(AccessActions.setAccess(access.id, access));
        addFlashAlert("success", "Commission values saved");
      } catch (response) {
        handleErrors(response);
      } finally {
        dispatch(IdsManagementActions.setModelLoaded("accesses"));
      }
    };
  },
  batchCheck: (orgId: number, userIds: number[]): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      try {
        dispatch(IdsManagementActions.fetchModel("batchCheck"));
        const response = await batchCheck(orgId, userIds);
        dispatch(AccessActions.receiveBatchCheck(orgId, response.results));
      } catch (errors) {
        handleErrors(errors);
      } finally {
        dispatch(IdsManagementActions.setModelLoaded("batchCheck"));
      }
    };
  },
};
